import { Controller } from "@hotwired/stimulus"

/**
 * Generic layout toggle for redesign A/B switches. Persists the chosen variant
 * in a cookie and reloads so the server renders the matching layout (resolved
 * by the `layout_preference` helper).
 *
 * Usage:
 *   <div data-controller="layout-toggle"
 *        data-layout-toggle-cookie-name-value="command_center_layout"
 *        data-layout-toggle-current-value="2026">
 *     <button data-layout-toggle-target="option"
 *             data-layout-toggle-layout-param="classic"
 *             data-action="layout-toggle#select">Clásico</button>
 *     <button data-layout-toggle-target="option"
 *             data-layout-toggle-layout-param="2026"
 *             data-action="layout-toggle#select">2026</button>
 *   </div>
 *
 * Pair with the shared/_layout_selector partial. See docs/frontend/layout-toggle.md.
 */
export default class extends Controller {
  static targets = ["option"]
  static values = {
    cookieName: String,
    current: String
  }

  static ACTIVE_CLASS = "bg-blue-100 border-blue-300 text-blue-700"
  static INACTIVE_CLASS = "bg-white"

  connect() {
    this.updateButtonStates()
  }

  select(event) {
    event.preventDefault()
    // Read the raw attribute (not event.params) so a numeric-looking variant
    // like "2026" isn't coerced to a Number and mis-compared against currentValue.
    const layout = event.currentTarget.dataset.layoutToggleLayoutParam
    if (!layout || layout === this.currentValue) return

    // Persist for a year and reload so the server renders the chosen layout.
    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 1)
    document.cookie = `${this.cookieNameValue}=${layout}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`

    window.location.reload()
  }

  updateButtonStates() {
    const activeClasses = this.constructor.ACTIVE_CLASS.split(" ")
    const inactiveClasses = this.constructor.INACTIVE_CLASS.split(" ")

    this.optionTargets.forEach((option) => {
      const isActive = option.dataset.layoutToggleLayoutParam === this.currentValue
      option.classList.remove(...(isActive ? inactiveClasses : activeClasses))
      option.classList.add(...(isActive ? activeClasses : inactiveClasses))
    })
  }
}
